import { useEffect, useState } from 'react';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props { session: Session; }

interface PlayerRating {
  rating: number;
  deviation: number;
  games_played: number;
}

interface OpponentCandidate {
  player_id: string;
  rating: number;
  level: number;
}

interface BracketMatch {
  match_id: string;
  slot_a: number;
  slot_b: number;
  winner: number | null;
}

interface BracketRound {
  round: number;
  matches: BracketMatch[];
}

export default function Matchmaking({ session }: Props) {
  const playerId = session.playerId;
  const [rating, setRating] = useState<PlayerRating | null>(null);
  const [opponents, setOpponents] = useState<OpponentCandidate[]>([]);
  const [bracket, setBracket] = useState<BracketRound[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Match form
  const [seasonId, setSeasonId] = useState('s1');
  const [opponentId, setOpponentId] = useState('');
  const [outcome, setOutcome] = useState('win');

  // Bracket form
  const [bracketIds, setBracketIds] = useState('');

  const base = `/api/v1/games/${session.gameId}/modules/matchmaking`;

  const reloadRating = () => {
    fetch(`${base}/player/${playerId}/rating`)
      .then(r => r.json())
      .then((d: { rating: PlayerRating }) => setRating(d.rating))
      .catch(e => setError(e.message));
  };

  const reloadOpponents = () => {
    fetch(`${base}/opponents/${playerId}`)
      .then(r => r.json())
      .then((d: { opponents: OpponentCandidate[] }) => setOpponents(d.opponents))
      .catch(e => setError(e.message));
  };

  useEffect(() => { reloadRating(); reloadOpponents(); }, [session.gameId, playerId]);

  const recordMatch = async () => {
    if (!seasonId || !opponentId.trim()) return;
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/match-result`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          season_id: seasonId,
          player_id: playerId,
          opponent_id: opponentId.trim(),
          outcome,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      reloadRating();
      reloadOpponents();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const genBracket = async () => {
    const ids = bracketIds.split(',').map(s => s.trim()).filter(Boolean);
    if (ids.length < 2) return;
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/bracket/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_ids: ids }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data: { rounds: BracketRound[] } = await res.json();
      setBracket(data.rounds);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <Container>
      <PageHeader
        title="Matchmaking"
        subtitle="ELO ratings, match results, brackets, and opponents."
      />
      {error && <div className="error-banner">{error}</div>}

      {/* Player Rating */}
      <div className="card mb-5 p-4">
        <h3 className="text-sm font-semibold mb-3">Player Rating</h3>
        {rating ? (
          <div className="flex gap-6 text-sm">
            <div><span className="text-fg-muted">Rating:</span> <strong>{Math.round(rating.rating)}</strong></div>
            <div><span className="text-fg-muted">Games:</span> {rating.games_played}</div>
            <div><span className="text-fg-muted">Deviation:</span> {Math.round(rating.deviation)}</div>
          </div>
        ) : (
          <div className="text-xs text-fg-muted">No rating yet</div>
        )}
      </div>

      {/* Record Match */}
      <div className="card mb-5 p-4">
        <h3 className="text-sm font-semibold mb-3">Record Match</h3>
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="text-xs text-fg-muted block mb-1">Season ID</label>
            <input className="input" value={seasonId} onChange={e => setSeasonId(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Opponent ID</label>
            <input className="input" placeholder="opponent_ulid" value={opponentId} onChange={e => setOpponentId(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Outcome</label>
            <select className="input" value={outcome} onChange={e => setOutcome(e.target.value)}>
              <option value="win">Win</option>
              <option value="loss">Loss</option>
              <option value="draw">Draw</option>
            </select>
          </div>
          <button className="btn btn-primary" disabled={busy || !opponentId.trim()} onClick={recordMatch}>Record</button>
        </div>
      </div>

      {/* Generate Bracket */}
      <div className="card mb-5 p-4">
        <h3 className="text-sm font-semibold mb-3">Tournament Bracket</h3>
        <div className="flex gap-3 items-end mb-3">
          <div className="flex-1">
            <label className="text-xs text-fg-muted block mb-1">Player IDs (comma-separated)</label>
            <input className="input w-full" placeholder="alice,bob,charlie,diana" value={bracketIds} onChange={e => setBracketIds(e.target.value)} />
          </div>
          <button className="btn btn-primary" disabled={busy} onClick={genBracket}>Generate</button>
        </div>
        {bracket.length > 0 && (
          <div className="space-y-3">
            {bracket.map(r => (
              <div key={r.round}>
                <div className="text-xs font-semibold text-fg-muted mb-1">Round {r.round}</div>
                <div className="flex gap-2 flex-wrap">
                  {r.matches.map(m => (
                    <div key={m.match_id} className="card p-2 text-xs">
                      <div className="font-mono">{m.match_id}</div>
                      <div>Seed {m.slot_a} vs Seed {m.slot_b}</div>
                      {m.winner && <div className="text-brand">Winner: Seed {m.winner}</div>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Opponents */}
      {opponents.length > 0 && (
        <div className="card">
          <h3 className="text-sm font-semibold p-4 pb-2">Nearby Opponents</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Player ID</th><th>Rating</th><th>Games</th></tr>
              </thead>
              <tbody>
                {opponents.map(o => (
                  <tr key={o.player_id}>
                    <td className="font-mono text-xs">{o.player_id}</td>
                    <td>{Math.round(o.rating)}</td>
                    <td>{o.level}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Container>
  );
}
